import { auth } from "@/lib/firebase"
import { useQuery } from "@tanstack/react-query"
import { DocumentStagesData } from "./useDocumentStages"

export interface BatchDocumentStagesResponse {
	[documentId: string]: DocumentStagesData
}

// Fetch stages for multiple documents at once
export const fetchBatchDocumentStages = async (
	caseId: string,
	documentIds: string[]
): Promise<BatchDocumentStagesResponse> => {
	const user = auth.currentUser
	if (!user) {
		throw new Error("User not found.")
	}
	const token = await user.getIdToken()
	
	// Fetch stages for each document in parallel
	const stagePromises = documentIds.map(async (documentId) => {
		const apiUrl = `${
			import.meta.env.VITE_API_URL
		}/api/cases/${caseId}/documents/${documentId}/stages/`
		
		const response = await fetch(apiUrl, {
			headers: { Authorization: `Bearer ${token}` },
		})
		
		if (!response.ok) {
			console.error(`Failed to fetch stages for document ${documentId}`)
			return null
		}
		
		const data: DocumentStagesData = await response.json()
		return { documentId, data }
	})
	
	const results = await Promise.all(stagePromises)
	
	// Convert array to object keyed by documentId
	const stagesMap: BatchDocumentStagesResponse = {}
	results.forEach((result) => {
		if (result) {
			stagesMap[result.documentId] = result.data
		}
	})
	
	return stagesMap
}

export const useBatchDocumentStages = (
	caseId: string | undefined,
	documentIds: string[]
) => {
	return useQuery<BatchDocumentStagesResponse, Error>({
		queryKey: ["batchDocumentStages", caseId, documentIds.sort().join(",")],
		queryFn: () => {
			if (!caseId || documentIds.length === 0) {
				return Promise.resolve({})
			}
			return fetchBatchDocumentStages(caseId, documentIds)
		},
		enabled: !!caseId && documentIds.length > 0,
		staleTime: 5 * 60 * 1000, // 5 minutes
		refetchOnWindowFocus: false,
		refetchInterval: 5000, // Refresh every 5 seconds
	})
}